import { Box, Button } from "@mui/material";
import { Stack } from "@mui/system";
import { useMutation } from "@tanstack/react-query";
import dayjs from "dayjs";
import moment from "moment";
import React, { useCallback, useEffect, useReducer, useState } from "react";
import {
  COLORS,
  DashboardTabs,
  EsgDatePickerField,
  Loading,
  MultiselectControlledField,
  Switch,
  usePagination,
  useTableExpandedRows,
  useYearMonthPeriodFilter,
  useYearPeriodFilter,
} from "components";
import { YearlyDateRangePicker } from "components/period-filters/year-period-filter/YearlyDateRangePicker";
import {
  updateSitesEmissionsViewAsOfDateMutation,
  useCountryRegionsSelect,
  useGetAllowedValues,
  useGetConfigParameter,
  useSitesMultiselect,
  useUtilityTypesMultiselect,
} from "hooks";
import { LOCAL_STORAGE_KEYS } from "consts";
import { ParsedQueryParams } from "types";
import { useNewBreadcrumb } from "recoils";
import { getDateFormatmonthset, getFirstAndLastDateOfFiscalYear, isValidDate } from "utils";
import { getSitesEmissions } from "api";
import { debounce } from "lodash";
import { ConsumptionSummary, AddDateModal, EmissionTableComponent, intensityColumns, locationBasedColumns, marketColumns } from "./components";
import { SITES_EMISSIONS_PAGE } from "./consts";

const currentDate = new Date();
const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
const lastDay = new Date(
  currentDate.getFullYear(),
  currentDate.getMonth() + 1,
  0,
);

const initialState = {
  isYearly: false,
  startDate: moment(firstDay).format("YYYY-MM-DD"),
  endDate: moment(lastDay).format("YYYY-MM-DD"),
  fromDate: null,
  toDate: null,
  view: "monthly",
};

const mainTabs = {
  INITIAL_TAB: "Summary",
};

const { INITIAL_TAB } = mainTabs;

function reducer(state: any, action: any) {
  switch (action.type) {
    case "SET_YEARLY": {
      // Use a block to declare `isYearly` and `period`
      const { isYearly, period } = action.payload;

      if (!period || !moment(period).isValid()) {
        console.error("Invalid period:", period);
        return state; // Return current state if period is invalid
      }

      return {
        ...state,
        isYearly,
        view: isYearly ? "yearly" : "monthly",
        startDate: isYearly
          ? moment(period).startOf("year").format("YYYY-MM-DD")
          : `${moment(period).format("YYYY-MM")}-01`,
        endDate: isYearly
          ? moment(period).endOf("year").format("YYYY-MM-DD")
          : `${moment(period).format("YYYY-MM")}-${moment(period).daysInMonth()}`,
      };
    }

    case "SET_FROM_DATE": {
      const fromDate = action.payload.value;

      if (!moment(fromDate, "YYYY-MM-DD", true).isValid()) {
        console.error("Invalid fromDate:", fromDate);
        return state;
      }

      return {
        ...state,
        fromDate,
        view: "daterange",
        startDate: fromDate,
      };
    }

    case "SET_TO_DATE": {
      const toDate = action.payload.value;

      if (!moment(toDate, "YYYY-MM-DD", true).isValid()) {
        console.error("Invalid toDate:", toDate);
        return state;
      }

      return {
        ...state,
        toDate,
        view: "daterange",
        endDate: toDate,
      };
    }

    case "SET_START_AND_END_DATES": {
      const { startDate, endDate } = action.payload;

      if (
        !moment(startDate, "YYYY-MM-DD", true).isValid() ||
        !moment(endDate, "YYYY-MM-DD", true).isValid()
      ) {
        console.error("Invalid startDate or endDate:", { startDate, endDate });
        return state;
      }

      return {
        ...state,
        startDate,
        endDate,
      };
    }

    case "CLEAR_DATES": {
      const startDate = moment().startOf("month");
      const endDate = moment().endOf("month");

      return {
        ...state,
        fromDate: null,
        toDate: null,
        startDate: startDate.format("YYYY-MM-DD"),
        endDate: endDate.format("YYYY-MM-DD"),
        isYearly: false,
        view: "monthly",
      };
    }

    default:
      return state;
  }
}

export const SitesEmissionsPage = () => {
  useNewBreadcrumb(SITES_EMISSIONS_PAGE.TITLE);

  const [state, dispatch] = useReducer(reducer, initialState);
  const [clearDate, setClearDate] = useState(false);
  const [dateError, setDateError] = useState(false);
  const [dateDisable, setDateDisable] = useState(false);
  const [dates, setDates] = useState<{ firstDate: string; lastDate: string } | null>(null);
  const [isFirstRender, setIsFirstRender] = useState(true);

  const { isYearly, startDate, endDate, fromDate, view } = state;

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const { year, onYearChange } = useYearPeriodFilter({
    updateExternalStates: undefined,
    withQueryParams: undefined,
    useYears: undefined,
    initialValue: dayjs().utc().year(),
  });

  // Fiscal Year 
  const { data } = useGetConfigParameter();
  const formattedStartMonth = data && data.find((obj) => obj.name === "company_month_start")?.configValue;
  const formattedEndMonth = data && data.find((obj) => obj.name === "company_month_end")?.configValue;

  const startMonth = (formattedStartMonth?.charAt(0) ?? '') + (formattedStartMonth?.slice(1)?.toLowerCase() ?? '');
  const endMonth = (formattedEndMonth?.charAt(0) ?? '') + (formattedEndMonth?.slice(1)?.toLowerCase() ?? '');

  useEffect(() => {
    const selectedYear = dayjs(year).format("YYYY")
    if (currentDate && startMonth) {
      const calculatedDates = getFirstAndLastDateOfFiscalYear(Number(selectedYear), startMonth, endMonth);
      setDates(calculatedDates);
    }
  }, [startMonth, endMonth, year]);

  useNewBreadcrumb(SITES_EMISSIONS_PAGE.TITLE);
  const { setInitialExpanded, updateQueryParamsWithInitialExpanded } =
    useTableExpandedRows();

  const { setInitialPage, updateQueryParamsWithInitialPage } = usePagination({
    pageSizeLocalStorageKey: LOCAL_STORAGE_KEYS.EMISSION_SUMMARY_SIZE,
    updateExternalStates: setInitialExpanded,
    getQueryParamsWithExternalChanges: updateQueryParamsWithInitialExpanded,
  });

  const updateExternalStates = useCallback(() => {
    setInitialPage();
    setInitialExpanded();
  }, [setInitialPage, setInitialExpanded]);

  const getQueryParamsWithExternalChanges = useCallback(
    (queryParams: ParsedQueryParams) =>
      updateQueryParamsWithInitialExpanded(
        updateQueryParamsWithInitialPage(queryParams),
      ),
    [updateQueryParamsWithInitialExpanded, updateQueryParamsWithInitialPage],
  );
  const { period, availableYears } = useYearMonthPeriodFilter({
    updateExternalStates,
    getQueryParamsWithExternalChanges,
    isYearly,
  });


  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState<any>([]);
  const [selectedCountryRegion, setSelectedCountryRegion] = useState<any>([]);

  const {
    siteIds,
    isSitesLoading,
    selectedSites,
    setSelectedSites,
    sitesOptions,
  } = useSitesMultiselect({ withQueryParams: false }, selectedBusinessUnit);

  const {
    utilityTypeIds,
    isUtilityTypesLoading,
    selectedUtilityTypes,
    setSelectedUtilityTypes,
    utilityTypesOptions,
  } = useUtilityTypesMultiselect({
    withQueryParams: false,
  });

  const [selected, setSelected] = React.useState('Monthly');

  const minDate = dayjs(
    `${Math.min(...(availableYears || [dayjs().utc().year()]))}-01-01`,
  ).utc();
  const maxDate = dayjs().endOf('day');
  const [dateFrom, setDateFrom] = useState("");

  const getCurrentMonthRange = () => {
    const now = new Date();
    const currentfirstDay = new Date(now.getFullYear(), now.getMonth(), 1); // First day of the month
    const currentlastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0); // Last day of the month

    return {
      fromDateValue: formatDate(currentfirstDay),
      toDateValue: formatDate(currentlastDay),
    };
  };

  const [dateFromToDate, setDateFromToDate] = useState<any>(getCurrentMonthRange());

  const handleFromDateChange = (date: any) => {
    setDateFrom(date);
    const firstDate = new Date(date);
    const firstDateOfMonth = new Date(
      firstDate.getFullYear(),
      firstDate.getMonth(),
      1,
    );
    const formattedFirstDate = firstDateOfMonth;
    const fromDateValue = moment(formattedFirstDate).format("YYYY-MM-DD");
    if (fromDateValue && dateFromToDate?.toDateValue < fromDateValue) {
      setDateError(true);
    } else {
      setDateError(false);
      setDateFromToDate({
        fromDateValue,
        toDateValue: dateFromToDate?.toDateValue,
      });
    }
  };

  const handleToDateChange = (date: any) => {
    const firstDate = new Date(dateFrom);
    const firstDateOfMonth = new Date(
      firstDate.getFullYear(),
      firstDate.getMonth(),
      1,
    );
    const formattedFirstDate = firstDateOfMonth;
    const fromDateValue = moment(formattedFirstDate).format("YYYY-MM-DD");

    const lastDate = new Date(date);
    const lastDateOfMonth = new Date(
      lastDate.getFullYear(),
      lastDate.getMonth() + 1,
      0,
    );
    const formattedLastDate = lastDateOfMonth;
    const toDateValue = moment(formattedLastDate).format("YYYY-MM-DD");

    // for get from date value hear code if user not select from date
    let formattedFirstDateOfYear;
    if (toDateValue) {
      const [Year] = toDateValue.split("-").map(Number);
      const firstDateOfYear = new Date(Year, 0, 1);
      formattedFirstDateOfYear = `${firstDateOfYear.getFullYear()}-${String(
        firstDateOfYear.getMonth() + 1,
      ).padStart(2, "0")}-${String(firstDateOfYear.getDate()).padStart(
        2,
        "0",
      )}`;
    }
    // end code
    if (fromDateValue && toDateValue < fromDateValue) {
      if (toDateValue && fromDateValue === "Invalid date") {
        setDateFromToDate({
          fromDateValue: formattedFirstDateOfYear,
          toDateValue,
        });
      } else {
        setDateError(true);
      }
    } else if (fromDate === null) {
      setDateFromToDate({ fromDateValue, toDateValue });

      setDateError(false);
    } else {
      setDateFromToDate({ fromDateValue, toDateValue });
      setDateError(false);
    }
  };

  useEffect(() => {
    if (dates && isFirstRender) {
      setSiteEmission({
        startDate: isYearly ? dates?.firstDate! : dateFromToDate?.fromDateValue,
        endDate: isYearly ? dates?.lastDate! : dateFromToDate?.toDateValue,
        siteIds,
        utilityTypeIds,
        businessUnits: selectedBusinessUnit.map((item: any) => item.value).join(","),
        viewType: view,
        countryRegions: selectedCountryRegion.map((item: any) => item.value).join(","),
        isDisaggregated: selected === "Monthly" ? "true" : "false",
      })
      setIsFirstRender(false)
    }
    // eslint-disable-next-line
  }, [dates])

  useEffect(() => {
    if (dates) {
      setSiteEmission({
        startDate: isYearly ? dates?.firstDate! : dateFromToDate?.fromDateValue,
        endDate: isYearly ? dates?.lastDate! : dateFromToDate?.toDateValue,
        siteIds,
        utilityTypeIds,
        businessUnits: selectedBusinessUnit.map((item: any) => item.value).join(","),
        viewType: view,
        countryRegions: selectedCountryRegion.map((item: any) => item.value).join(","),
        isDisaggregated: selected === "Monthly" ? "true" : "false",
      })
    }
    // eslint-disable-next-line
  }, [isYearly, selected])

  const handleSearchDates = () => {
    if (
      fromDate === null &&
      dateFromToDate?.fromDateValue !== undefined &&
      dateFromToDate?.toDateValue !== undefined
    ) {
      dispatch({
        type: "SET_FROM_DATE",
        payload: { value: dateFromToDate?.fromDateValue, period },
      });
      dispatch({
        type: "SET_TO_DATE",
        payload: { value: dateFromToDate?.toDateValue, period },
      });
      setDateDisable(true);
    } else if (
      dateFromToDate?.fromDateValue !== undefined &&
      dateFromToDate?.toDateValue !== undefined
    ) {
      dispatch({
        type: "SET_FROM_DATE",
        payload: { value: dateFromToDate?.fromDateValue, period },
      });
      dispatch({
        type: "SET_TO_DATE",
        payload: { value: dateFromToDate?.toDateValue, period },
      });
      setDateDisable(true);
    } else {
      const firstDate = new Date(dateFrom);
      const firstDateOfMonth = new Date(
        firstDate.getFullYear(),
        firstDate.getMonth(),
        1,
      );
      const formattedFirstDate = firstDateOfMonth;

      dispatch({
        type: "SET_FROM_DATE",
        payload: {
          value: moment(formattedFirstDate).format("YYYY-MM-DD"),
          period,
        },
      });

    }
    setSiteEmission({
      startDate: isYearly ? dates?.firstDate! : dateFromToDate?.fromDateValue,
      endDate: isYearly ? dates?.lastDate! : dateFromToDate?.toDateValue,
      siteIds,
      utilityTypeIds,
      businessUnits: selectedBusinessUnit.map((item: any) => item.value).join(","),
      viewType: view,
      countryRegions: selectedCountryRegion.map((item: any) => item.value).join(","),
      isDisaggregated: selected === "Monthly" ? "true" : "false",
    })
    setDateDisable(false);
  };

  const handleClearDates = () => {
    dispatch({ type: "CLEAR_DATES", payload: null });
    setDateError(false);
    setClearDate(true);
    setTimeout(() => {
      setClearDate(false);
    }, 100);
    window.location.reload();
  };

  const clearDates = () => {
    setSelectedCountryRegion("");

    dispatch({ type: "CLEAR_DATES", payload: null });
    setDateError(false);
    setClearDate(true);
    setDateFrom("");
    setTimeout(() => {
      setClearDate(false);
    }, 100);
    window.location.reload();
  };

  const handleSwitchChange = () => {
    dispatch({ type: "SET_YEARLY", payload: { isYearly: !isYearly, period } });
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    isYearly ? clearDates() : null;
  };

  useEffect(() => {
    dispatch({
      type: "SET_START_AND_END_DATES",
      payload: {
        startDate: isYearly
          ? year?.startOf("year").format("YYYY-MM-DD")
          : `${period.format("YYYY-MM")}-01`,
        endDate: isYearly
          ? year?.endOf("year").format("YYYY-MM-DD")
          : `${period.format("YYYY-MM")}-${period?.daysInMonth()}`,
      },
    });
    // eslint-disable-next-line
  }, [period, year]);

  const [siteFilter, setSiteFilter] = useState("");
  const [siteView, setSiteView] = useState(false);
  const [viewAsOfData, setViewAsOfData] = useState<any>([]);

  const { data: sitesEmissions, isLoading, mutateAsync: setSiteEmission } = useMutation(getSitesEmissions)

  // Update Mutation
  const updateMutation = useMutation(updateSitesEmissionsViewAsOfDateMutation);
  const { data: allowedValues } = useGetAllowedValues();

  const businessUnits = allowedValues?.businessUnits?.map((t) => ({
    value: t,
    label: t,
  }));

  useEffect(() => {
    const fetchData = async () => {
      updateMutation
        .mutateAsync({
          startDate,
          endDate,
          viewType: view,
          siteIds,
          utilityTypeIds,
          viewAsOfDate: moment(siteFilter).format("YYYY-MM-DD"),
        })
        .then((apiData: any) => {
          setViewAsOfData(apiData);
        })
        .catch((error: any) => {
          console.error("Error fetching data:", error);
        });
    };
    if (siteFilter !== "") {
      setSiteView(true);
      fetchData();
      setTimeout(() => {
        setSiteView(false);
      }, 1500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteFilter, isYearly]);

  const viewAsDate = (dateValue: any) => {
    setSiteFilter(dateValue);
  };

  const tableData: any = siteView
    ? viewAsOfData && viewAsOfData !== null
      ? viewAsOfData
      : []
    : sitesEmissions?.siteAggregations || [];

  const { countryRegionsList } = useCountryRegionsSelect({
    updateExternalStates: setInitialPage,
    withEmptyOption: false,
  });

  const countryRegionsListData = countryRegionsList?.map((t) => ({
    value: t,
    label: t,
  }));

  const debouncedHandleFromDateChange = debounce(handleFromDateChange, 500);
  const debouncedHandleToDateChange = debounce(handleToDateChange, 500);

  const allTabs: any = {
    [INITIAL_TAB]: [
      {
        label: "Summary",
        content: <ConsumptionSummary
          sitesEmissions={sitesEmissions!}
          isLoading={!(sitesEmissions && !isLoading)}
        />,
      },
      {
        label: "Location Based",
        content: isLoading ? <Loading /> : <EmissionTableComponent
          complementaryColumns={locationBasedColumns}
          selected={selected}
          setSelected={setSelected}
          tableData={tableData}
        />,
      },
      {
        label: "Market Based",
        content: isLoading ? <Loading /> : <EmissionTableComponent
          complementaryColumns={marketColumns}
          selected={selected}
          setSelected={setSelected}
          tableData={tableData}
        />,
      },
      {
        label: "Emissions Intensity",
        content: isLoading ? <Loading /> : <EmissionTableComponent
          complementaryColumns={intensityColumns}
          selected={selected}
          setSelected={setSelected}
          tableData={tableData}
        />,
      },
      // {
      //   label: "Allocation",
      //   content: isLoading ? <Loading /> : <EmissionTableComponent
      //     complementaryColumns={allocationColumns}
      //     selected={selected}
      //     setSelected={setSelected}
      //     tableData={tableData}
      //   />,
      // },
    ],
  };

  return (
    <Box sx={{ marginLeft: 3, pt: 1.5 }}>
      <Box
        sx={{
          "@media (max-width: 990px)": {
            left: "345px",
          },
          "@media (max-width: 1350px)": {
            right: "490px",
          },
          "@media (min-width: 1350px) and (max-width: 1450px)": {
            left: "710px",
          },
          "@media (min-width: 1450px)": {
            right: "590px",
          },
          width: "160px",
          "& span:first-child > button": {
            width: "160px",
          },
          position: "absolute",
          top: "-5px",
          right: isLoading ? "500px" : "420px",
          cursor: "pointer",
        }}
      >
        <AddDateModal
          viewAsDate={viewAsDate}
          disabled={!isYearly}
          year={year}
        />
      </Box>
      <Box sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 2, mb: 2
      }}>
        <Box sx={{ mt: isYearly ? 3 : 3 }}>
          <Switch
            isChecked={isYearly}
            label={isYearly ? "Year" : "Range"}
            onChange={handleSwitchChange}
          />
        </Box>
        {isYearly && (
          <Box sx={{ width: "160px" }}>
            <YearlyDateRangePicker
              label="Year"
              year={year}
              onYearChange={onYearChange}
              availableYears={availableYears}
            />
          </Box>
        )}

        <Box sx={{ minWidth: "180px", width: "fit-content" }}>
          <MultiselectControlledField
            disabled={isUtilityTypesLoading}
            label="Utility type"
            selectedValues={selectedUtilityTypes}
            setSelectedValues={setSelectedUtilityTypes}
            options={utilityTypesOptions}
            isAllSelectedOnEmptySelection
          />
        </Box>
        <Box sx={{ minWidth: "180px", width: "fit-content" }}>
          <MultiselectControlledField
            label="Sites"
            disabled={isSitesLoading}
            selectedValues={selectedSites}
            setSelectedValues={setSelectedSites}
            options={sitesOptions}
            isAllSelectedOnEmptySelection
          />
        </Box>
        {businessUnits && (
          <Box sx={{ minWidth: "180px", width: "fit-content" }}>
            <MultiselectControlledField
              label="Business Unit"
              disabled={isSitesLoading}
              selectedValues={selectedBusinessUnit}
              setSelectedValues={setSelectedBusinessUnit}
              options={businessUnits as any}
              isAllSelectedOnEmptySelection
            />
          </Box>
        )}
        {countryRegionsList && (
          <Box sx={{ minWidth: "180px", width: "fit-content" }}>
            <MultiselectControlledField
              label="Region"
              selectedValues={selectedCountryRegion}
              setSelectedValues={setSelectedCountryRegion}
              options={countryRegionsListData as any}
              isAllSelectedOnEmptySelection
            />
          </Box>
        )}
        {!isYearly && (
          <Stack
            direction="row"
            spacing={2}
            sx={{ width: dateError ? "30%" : "22%" }}
          >
            <EsgDatePickerField
              label="From Month"
              views={["year", "month"]}
              openTo="month"
              minDate={minDate}
              maxDate={maxDate}
              format={getDateFormatmonthset()}
              disableHighlightToday
              disableFuture
              onChange={(newValue: any) => {
                const firstDate = new Date(newValue);
                const firstDateOfMonth = new Date(
                  firstDate.getFullYear(),
                  firstDate.getMonth(),
                  1,
                );
                const formattedFirstDate = firstDateOfMonth;
                const fromDateValue = moment(formattedFirstDate).format("YYYY-MM-DD");

                const isDateValid = isValidDate(fromDateValue)
                if (isDateValid) debouncedHandleFromDateChange(newValue);
              }}
              onAccept={handleFromDateChange}
              value={fromDate}
              clearDate={clearDate}
              placeholdervalue="MM-YYYY"
              disabled={dateDisable}
              emission
            />
            <EsgDatePickerField
              label="To Month"
              views={["year", "month"]}
              openTo="year"
              minDate={minDate}
              maxDate={maxDate}
              format={getDateFormatmonthset()}
              disableHighlightToday
              disableFuture
              onChange={(newValue: any) => {
                const firstDate = new Date(newValue);
                const firstDateOfMonth = new Date(
                  firstDate.getFullYear(),
                  firstDate.getMonth(),
                  1,
                );
                const formattedFirstDate = firstDateOfMonth;
                const fromDateValue = moment(formattedFirstDate).format("YYYY-MM-DD");

                const isDateValid = isValidDate(fromDateValue)
                if (isDateValid) debouncedHandleToDateChange(newValue);
              }}
              onAccept={handleToDateChange}
              value={fromDate}
              clearDate={clearDate}
              placeholdervalue="MM-YYYY"
              disabled={dateDisable}
              emission
            />
            {dateError && (
              <Box sx={{ color: COLORS.Fuzzy_Brown, fontSize: "0.75rem" }}>
                Invalid date selection. Please choose a date after the From
                Date.
              </Box>
            )}
          </Stack>
        )}
        <Button
          sx={{
            width: "100px",
            paddingTop: "10px",
            border: "1px solid #000",
            marginTop: "22px",
          }}
          onClick={handleSearchDates}
          disabled={dateError}
        >
          Search
        </Button>
        <Button
          sx={{
            width: "120px",
            paddingTop: "10px",
            border: "1px solid #000",
            marginTop: "22px",
          }}
          onClick={handleClearDates}
        >
          Clear Dates
        </Button>
      </Box>
      <DashboardTabs intensity tabs={allTabs[INITIAL_TAB]} selectedBtnOption={mainTabs.INITIAL_TAB} dashboard />
    </Box>
  );
};

export default SitesEmissionsPage;
